import { Plugin } from 'ckeditor5';

export default class CustomMentionPlugin extends Plugin {
    static get pluginName() {
        return 'CustomMentionPlugin';
    }

    static get requires() {
        return ['Mention'];
    }

    init() {
        const editor = this.editor;

        // === USER MENTIONS ===
        editor.conversion.for('upcast').elementToAttribute({
            view: {
                name: 'span',
                key: 'data-mention',
                classes: 'mention',
                attributes: {
                    'data-user-username': true
                }
            },
            model: {
                key: 'mention',
                value: viewItem =>
                    editor.plugins.get('Mention').toMentionAttribute(viewItem, {
                        userId: viewItem.getAttribute('data-user-username')
                    })
            },
            converterPriority: 'high'
        });

        editor.conversion.for('upcast').elementToElement({
            view: { name: 'div', classes: 'zoomable-image' },
            model: (viewEl, { writer }) => {
                // Preserve ALL classes (e.g., "zoomable-image mention")
                const classes = viewEl.getClassNames
                    ? Array.from(viewEl.getClassNames()).join(' ')
                    : 'zoomable-image';

                // Ensure "zoomable-image" is present even if order is different
                const classAttr = classes.includes('zoomable-image')
                    ? classes
                    : `zoomable-image ${classes}`;

                return writer.createElement('div', {
                    class: classAttr,
                    'data-image-id': viewEl.getAttribute('data-image-id')
                });
            },
            converterPriority: 'high'
        });


        editor.conversion.for('downcast').attributeToElement({
            model: 'mention',
            view: (modelAttributeValue, { writer }) => {
                if (!modelAttributeValue) return null;
                if (modelAttributeValue.id && modelAttributeValue.id[0] === '@') {
                    return writer.createAttributeElement(
                        'a',
                        {
                            class: 'mention',
                            'data-mention': modelAttributeValue.id,
                            'data-user-username': modelAttributeValue.username
                        },
                        {
                            priority: 20,
                            id: modelAttributeValue.uid
                        }
                    );
                }
                return null;
            },
            converterPriority: 'high'
        });

        // === Helpers ===
        const buildZoomableImageBlock = (writer, imageId, src, title) => {
            const captionText = '#' + imageId;

            const zoomableDiv = writer.createElement('div', {
                class: 'zoomable-image mention',
                'data-image-id': imageId,
                contenteditable: 'false'
            });

            const figure = writer.createElement('figure', {
                contenteditable: 'false'
            });

            const image = writer.createElement('zoomableImage', {
                src,
                loading: 'lazy',
                alt: title || ''
            });

            const caption = writer.createElement('span', {
                class: 'image-caption',
                'data-image-id': imageId,
                contenteditable: 'false',
                draggable: 'false'
            });
            writer.insertText(captionText, caption);

            writer.append(image, figure);
            writer.append(caption, figure);
            writer.append(figure, zoomableDiv);

            return zoomableDiv;
        };

        // Remove preceding paragraph if it only contains the raw trigger like "#33"
        const removeTriggerParagraphIfFound = (writer, blockElement, rawId) => {
            const parent = blockElement.parent;
            if (!parent) return;

            const children = Array.from(parent.getChildren());
            const idx = children.indexOf(blockElement);
            if (idx <= 0) return;

            const prev = children[idx - 1];
            // We're looking for a paragraph block containing only the trigger text or mention span.
            if (!prev.is('element')) return;

            // If it's a paragraph
            if (prev.name === 'paragraph') {
                const innerTextPieces = [];
                for (const child of prev.getChildren()) {
                    if (child.is('text')) {
                        innerTextPieces.push(child.data);
                    } else if (child.is('element') && child.hasAttribute && child.getAttribute('data-mention')) {
                        for (const grandChild of child.getChildren()) {
                            if (grandChild.is('text')) innerTextPieces.push(grandChild.data);
                        }
                    }
                }
                const combined = innerTextPieces.join('').trim();
                if (combined === rawId) {
                    writer.remove(writer.createRangeOn(prev));
                }
            }
        };

        // === Main: handle mention event ===
        const mentionPlugin = editor.plugins.get('Mention');
        if (mentionPlugin) {
            mentionPlugin.on('mention', (evt, data) => {
                if (
                    !data ||
                    !data.item ||
                    typeof data.item.id !== 'string' ||
                    !data.item.id.startsWith('#')
                ) {
                    return;
                }

                evt.stop(); // prevent default inline insertion

                const rawId = data.item.id; // e.g. "#338"
                const imageId = rawId.replace('#', '');
                const src = data.item.thumbnail_link || `/images/thumbs/${imageId}.jpg`;
                const title = data.item.title || '';

                editor.model.change(writer => {
                    let insertPosition = null;
                    if (data.range) {
                        writer.remove(data.range);
                        insertPosition = data.range.start;
                    }

                    const block = buildZoomableImageBlock(writer, imageId, src, title);
                    if (insertPosition) {
                        editor.model.insertContent(block, insertPosition);
                    } else {
                        editor.model.insertContent(block);
                    }

                    // Clean up leftover "#33" paragraph if any right before the inserted block
                    removeTriggerParagraphIfFound(writer, block, rawId);
                });
            });
        }

        // === Fallback: override mention command ===
        const mentionCommand = editor.commands.get('mention');
        if (mentionCommand) {
            const originalExecute = mentionCommand.execute.bind(mentionCommand);
            mentionCommand.execute = options => {
                if (
                    options &&
                    options.mention &&
                    typeof options.mention.id === 'string' &&
                    options.mention.id.startsWith('#')
                ) {
                    const rawId = options.mention.id;
                    const imageId = rawId.replace('#', '');
                    const src = options.mention.thumbnail_link || `/images/thumbs/${imageId}.jpg`;
                    const title = options.mention.title || '';

                    editor.model.change(writer => {
                        const block = buildZoomableImageBlock(writer, imageId, src, title);
                        editor.model.insertContent(block);
                        removeTriggerParagraphIfFound(writer, block, rawId);
                    });
                } else {
                    originalExecute(options);
                }
            };
        }
    }
}
